import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'lodash';
import { useIdentify } from '@hungryroot/customer';
import {
  type CustomerFoodFeedback,
  type CustomerFoodFeedbackPreference,
} from '@hungryroot/rest';
import { useDeleteCustomerFoodFeedback } from '../../../hooks/useDeleteCustomerFoodFeedback';
import { usePostCustomerFoodFeedback } from '../../../hooks/usePostCustomerFoodFeedback';
import {
  useSelfFoodFeedback,
  getQueryKey as getSelfFoodFeedbackQueryKey,
  invalidateQuery as invalidateSelfFoodFeedbackQuery,
} from '../../../hooks/useSelfFoodFeedback';
import { useSelfFoodFeedbacks } from '../../../hooks/useSelfFoodFeedbacks';
import { useOptimisticUpdateStatus } from '../useOptimisticUpdateStatus';
import {
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';

type UseFoodFeedbackUpdatesProps = {
  /**
   * Whether or not to allow API calls. This is useful for cases where consumers of this
   * hook are waiting for dependencies to be ready before making these API calls. Disabling
   * this hook will prevent any API call from being made but will keep track of what
   * call should be made after it's been enabled since th user can still interact with
   * the UI due to the optimistic nature of the `toggled` state.
   */
  enabled: boolean;

  /** Error handler to run when the API comes across an issue. */
  onError?: () => void;

  /** Information about the pairing or product. */
  item: RatableProductItem | RatablePairingItem;

  /**
   * Whether or not to fetch the initial rating from the server. This is useful for cases
   * where we know this should exist.
   *
   * @default false
   */
  enableInitialFetch?: boolean;

  /**
   * The ID of the food feedback item. Not to be mistaken with the customer food feedback
   * ID which is specific to the customer's feedback.
   */
  foodFeedbackId: number;

  /**
   * Whether or not the creation of the customer food feedback represent an addition to
   * their list of liked or disliked list.
   */
  foodFeedbackPreference: CustomerFoodFeedbackPreference;

  /**
   * Callback to run when the creation/destruction of the customer food feedback is successful.
   */
  onSuccessfulChange?: ({
    customerFoodFeedbackId,
    foodFeedbackId,
  }: {
    customerFoodFeedbackId: number | null;
    foodFeedbackId: number;
  }) => void;
};

/**
 * Handles optimistic updates and API calls for food feedback.
 */
export function useFoodFeedbackUpdates(props: UseFoodFeedbackUpdatesProps) {
  const {
    item,
    enabled,
    onError,
    enableInitialFetch = false,
    foodFeedbackId,
    foodFeedbackPreference,
    onSuccessfulChange,
  } = props;

  const {
    optimisticUpdateStatus,
    customerFoodFeedbackId,
    selfFoodFeedbackData,
    localIsActive,
    setLocalIsActive,
    postCustomerFoodFeedbackMutate,
    deleteCustomerFoodFeedbackMutate,
  } = useFoodFeedbackMutations({
    onError,
    enableInitialFetch,
    onSuccessfulChange,
    foodFeedbackId,
    item,
  });

  const latestApiCallRef = useRef<(() => void) | null>(null);

  // This effect is used to make API calls in the case where `enabled` goes from
  // `false` to `true`. This allows the user to continue doing what they want to
  // do on the UI while we wait for any dependencies to be ready before this hook
  // is usable.
  useEffect(() => {
    // Nothing to do here since the user hasn't done anything or has gone through
    // a series of steps that should result in no API calls.
    if (!latestApiCallRef.current) {
      return;
    }

    // Dependencies are completed, run the latest API call we should have made.
    if (enabled) {
      latestApiCallRef.current();
      latestApiCallRef.current = null;
    }
  }, [enabled]);

  const { customerData } = useIdentify();

  /**
   * Throttled toggle handler. Automatically determines which API call to make
   * (if any) and manages optimistic updates when API calls are disabled.
   */
  const memoizedOnToggleApiCall = useMemo(
    () =>
      throttle(() => {
        if (!customerData?.id) {
          return;
        }

        const createFoodFeedback = () =>
          postCustomerFoodFeedbackMutate({
            customer_id: customerData.id,
            requestBody: {
              food_feedback_id: foodFeedbackId,
              preference: foodFeedbackPreference,
              bundle_id: 'bundleId' in item ? item.bundleId : null,
              product_id: 'productId' in item ? item.productId : null,
              pairing_id: 'pairingId' in item ? item.pairingId : null,
              order_id: item.orderNumber,
              tags: [],
            },
          });

        const deleteFoodFeedback = (customerFoodFeedbackId: number) =>
          deleteCustomerFoodFeedbackMutate({
            customer_id: customerData.id,
            id: customerFoodFeedbackId,
          });

        if (enabled) {
          if (!selfFoodFeedbackData) {
            createFoodFeedback();
          } else if (customerFoodFeedbackId) {
            deleteFoodFeedback(customerFoodFeedbackId);
          }
        } else {
          // API calls haven't been enabled yet but the user will still be doing things.
          // Keep track of the latest API call so when the hook is enabled, we can
          // run it.
          if (!selfFoodFeedbackData) {
            setLocalIsActive(true);
            latestApiCallRef.current = () => createFoodFeedback();
          } else if (customerFoodFeedbackId) {
            setLocalIsActive(false);
            latestApiCallRef.current = () =>
              deleteFoodFeedback(customerFoodFeedbackId);
          } else {
            latestApiCallRef.current = null;
          }
        }
      }, 1000),
    [
      enabled,
      customerData?.id,
      customerFoodFeedbackId,
      setLocalIsActive,
      foodFeedbackId,
      foodFeedbackPreference,
      item,
      selfFoodFeedbackData,
      postCustomerFoodFeedbackMutate,
      deleteCustomerFoodFeedbackMutate,
    ]
  );

  return {
    // Existance of the specific customer food feedback here indicates that it is active.
    toggled: selfFoodFeedbackData ? true : localIsActive,
    onToggle: memoizedOnToggleApiCall,
    optimisticUpdateStatus,
    customerFoodFeedbackId: selfFoodFeedbackData?.id,
  };
}

type UseFoodFeedbackMutationsProps = {
  onError?: () => void;
  enableInitialFetch?: boolean;
  onSuccessfulChange?: ({
    customerFoodFeedbackId,
    foodFeedbackId,
  }: {
    customerFoodFeedbackId: number | null;
    foodFeedbackId: number;
  }) => void;
  foodFeedbackId: number;
  item: RatableProductItem | RatablePairingItem;
};

function useFoodFeedbackMutations(props: UseFoodFeedbackMutationsProps) {
  const {
    onError,
    enableInitialFetch,
    onSuccessfulChange,
    foodFeedbackId,
    item,
  } = props;

  // Keeps track of whether or not the user has activated their food feedback
  // at least once. This is used to determine whether or not we should initally
  // fetch the latest rating from the server.
  const [hasRated, setHasRated] = useState(enableInitialFetch);

  // Keeps track of the customer food feedback ID for the current item.
  const [customerFoodFeedbackId, setCustomerFoodFeedbackId] = useState<
    number | null
  >(null);

  const { data: selfFoodFeedbackData, error: selfFoodFeedbackError } =
    useSelfFoodFeedback({
      params: {
        id: customerFoodFeedbackId as number,
      },
      options: {
        enabled: hasRated && Number.isInteger(customerFoodFeedbackId),
      },
    });

  useEffect(() => {
    if (selfFoodFeedbackError && customerFoodFeedbackId) {
      setCustomerFoodFeedbackId(null);
    }
  }, [customerFoodFeedbackId, selfFoodFeedbackError]);

  const { data: selfFoodFeedbacksData, isPending: selfFoodFeedbacksIsPending } =
    useSelfFoodFeedbacks({
      params: {
        order: item.orderNumber,
        bundle: 'bundleId' in item && item.bundleId ? item.bundleId : undefined,
        pairing:
          'pairingId' in item && item.pairingId ? item.pairingId : undefined,
      },
      options: {
        enabled: enableInitialFetch,
      },
    });

  useEffect(() => {
    if (!selfFoodFeedbacksIsPending && selfFoodFeedbacksData) {
      const foundFoodFeedbackData = selfFoodFeedbacksData.results.find(
        (customerFoodFeedback) => {
          return customerFoodFeedback.food_feedback_id === foodFeedbackId;
        }
      );

      if (foundFoodFeedbackData) {
        setCustomerFoodFeedbackId(foundFoodFeedbackData.id);
      }
    }
  }, [selfFoodFeedbacksIsPending, selfFoodFeedbacksData, foodFeedbackId]);

  // Temporary state to keep track of whether or not the food feedback item is active.
  const [localIsActive, setLocalIsActive] = useState<boolean>(false);

  const { optimisticUpdateStatus, setOptimisticUpdateStatus } =
    useOptimisticUpdateStatus();

  const queryClient = useQueryClient();

  const { mutate: postCustomerFoodFeedbackMutate } =
    usePostCustomerFoodFeedback({
      onMutate: () => {
        setOptimisticUpdateStatus('pending');
        setLocalIsActive(true);
      },

      onSuccess: (data) => {
        // Pre-populate the cache with the successful response.
        const queryKey = getSelfFoodFeedbackQueryKey({ id: data.id });

        queryClient.setQueryData(queryKey, data);

        // We have a product rating ID now, update the local active state.
        setLocalIsActive(true);
        setCustomerFoodFeedbackId(data.id);
        onSuccessfulChange?.({
          customerFoodFeedbackId: data.id,
          foodFeedbackId: data.food_feedback_id,
        });
      },

      onError: () => {
        onError?.();
        setLocalIsActive(false);
      },

      onSettled: (data) => {
        setOptimisticUpdateStatus('settled');
        setHasRated(true);

        if (data) {
          invalidateSelfFoodFeedbackQuery({ id: data.id, queryClient });
        }
      },
    });

  const { mutate: deleteCustomerFoodFeedbackMutate } =
    useDeleteCustomerFoodFeedback({
      onMutate: async () => {
        setOptimisticUpdateStatus('pending');
        setLocalIsActive(false);

        if (customerFoodFeedbackId) {
          const queryKey = getSelfFoodFeedbackQueryKey({
            id: customerFoodFeedbackId,
          });

          await queryClient.cancelQueries({ queryKey });

          const previousCustomerFoodFeedback: CustomerFoodFeedback | undefined =
            queryClient.getQueryData(queryKey);

          queryClient.setQueryData(queryKey, null);

          return { previousCustomerFoodFeedback };
        }
      },

      onSuccess: () => {
        if (customerFoodFeedbackId) {
          const queryKey = getSelfFoodFeedbackQueryKey({
            id: customerFoodFeedbackId,
          });
          queryClient.removeQueries({ queryKey });
        }

        setLocalIsActive(false);
        setCustomerFoodFeedbackId(null);

        onSuccessfulChange?.({
          customerFoodFeedbackId: null,
          foodFeedbackId,
        });
      },

      onError: (_error, _variables, context) => {
        if (context && customerFoodFeedbackId) {
          const queryKey = getSelfFoodFeedbackQueryKey({
            id: customerFoodFeedbackId,
          });

          queryClient.setQueryData(
            queryKey,
            context.previousCustomerFoodFeedback
          );
        }

        onError?.();

        // Invalidate the cache to make sure the UI reflects the server state just
        // in case the user is trying to do something like de-activate a stale food
        // feedback.
        if (customerFoodFeedbackId) {
          invalidateSelfFoodFeedbackQuery({
            id: customerFoodFeedbackId,
            queryClient,
          });
        }
      },

      onSettled: () => {
        setOptimisticUpdateStatus('settled');
      },
    });

  return {
    optimisticUpdateStatus,
    customerFoodFeedbackId,
    hasRated,
    localIsActive,
    setLocalIsActive,
    postCustomerFoodFeedbackMutate,
    deleteCustomerFoodFeedbackMutate,
    selfFoodFeedbackData: selfFoodFeedbackError
      ? undefined
      : selfFoodFeedbackData,
  };
}
